import React, { PropsWithChildren, Children, ReactNode } from 'react';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { Label } from '../Label/Label';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import { Option } from './Option';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { SelectParam } from '../../types/SelectParam';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { FieldWrapper } from '../../grid/FieldWrapper/FieldWrapper';
import { Input } from '../Input/Input';

type OptionLike = { value: string };
type HTMLSelectProps = React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>;
type Select<TOption extends OptionLike> = {
  label: ReactNode;
  helpText?: string;
  labelHidden?: boolean;
  param?: SelectParam<string, TOption>;
} & Omit<HTMLSelectProps, 'id' | 'ref' | 'className'>;

function normalizeParamsToHTMLAttributes<TOption extends OptionLike>(
  selectProps: HTMLSelectProps,
  param?: SelectParam<string, TOption>,
) {
  return {
    required: param?.required,
    defaultValue: param?.value,
    name: param?.name,
    ...selectProps,
  };
}
export function Select<TOption extends OptionLike>({
  label,
  helpText,
  labelHidden,
  children,
  param,
  ...selectProps
}: PropsWithChildren<Select<TOption>>) {
  const { t } = useSonTranslation('phrases');
  const id = useHtmlValidId();

  const selectId = `select-${id}`;
  const helpTextId = `select-help-text-${id}`;

  const htmlAttributes = normalizeParamsToHTMLAttributes(selectProps, param);

  if (Children.count(children) === 0) {
    return null;
  }

  if (Children.count(children) === 1 && htmlAttributes.required) {
    const option = Array.isArray(children) ? children[0] : children;
    const textToShow = typeof option.props.children === 'string' ? option.props.children : option.props.value;
    return (
      <>
        <input type="hidden" name={htmlAttributes.name} value={option.props.value} />
        <Input label={label} defaultValue={textToShow} readOnly required />
      </>
    );
  }

  return (
    <ElementContainer>
      {!labelHidden && (
        <Label htmlFor={selectId} required={htmlAttributes?.required} helpText={helpText} helpTextId={helpTextId}>
          {label}
        </Label>
      )}
      <FieldWrapper>
        <span className="form2-select__container">
          <select
            id={selectId}
            className="form2-select"
            aria-describedby={!labelHidden && helpText ? helpTextId : undefined}
            aria-label={labelHidden ? label?.toString() : undefined}
            {...htmlAttributes}
          >
            {/* All selects with that are required need to have an option with an empty value,
           which makes the selection invalid */}
            {htmlAttributes?.required && (
              <Option key="default" value="">
                {t('selectPlaceholderOptionLabel')}
              </Option>
            )}
            {children}
          </select>
          <span className="form2-select__button">
            <SVG svgSpriteId={Icons.ARROW_DOWN} />
          </span>
        </span>
      </FieldWrapper>
    </ElementContainer>
  );
}
